import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// Confirming / AP (Aviso de Pagamento) — GET/POST/PUT/DELETE /api/confirming-payments
// Tipos escritos à mão a partir do contrato §6 (camelCase pinado). Não depende do web-api-client.

// ConfirmingPaymentDto — um AP (fatura de fornecedor financiada via Confirming).
export interface ConfirmingPayment {
  id: number;
  workFinancialEntryId: number;
  workId: number | null;
  workName: string | null;
  supplierId: number | null;
  supplierName: string;
  invoiceNumber: string | null;
  amount: number;
  issueDate: string;
  dueDate: string;
  annualRatePercent: number;
  termDays: number;
  financialCost: number;
  statusId: number;
  statusCode: string;
  statusName: string;
  currencyCode: string;
  notes: string | null;
  /** Nº do Aviso de Pagamento dado pelo banco. */
  reference?: string | null;
  /** Dia em que o banco debitou a liquidação (vem da reconciliação bancária). */
  settledOn?: string | null;
  /** As faturas que o aviso paga (uma ou várias do mesmo fornecedor). */
  invoices?: ConfirmingPaymentInvoice[];
  confirmingBankId?: number | null;
  confirmingBankName?: string | null;
}

// ConfirmingPlafondVm [3.4.3] — plafond em tempo real.
export interface ConfirmingPlafond {
  limit: number;
  used: number;
  available: number;
  activeCount: number;
  totalFinancialCost: number;
  /** Plafond de cada banco (vazio sem bancos configurados). */
  banks?: ConfirmingBankPlafond[];
}

export interface ConfirmingBankPlafond {
  bankId: number | null;
  name: string;
  limit: number;
  used: number;
  available: number;
  activeCount: number;
}

/** Banco com linha de Confirming: plafond, prazo e contrato próprios (Definições › Tesouraria). */
export interface ConfirmingBank {
  /** 0 = novo. */
  id: number;
  name: string;
  plafond: number;
  termDays: number;
  /** Prazo sempre igual (a liquidação calcula-se); senão é só ponto de partida. */
  isTermFixed: boolean;
  contractReference: string | null;
  sortOrder: number;
}

// EligibleSupplierInvoiceDto — fatura de fornecedor (Expense) ainda sem AP.
export interface EligibleSupplierInvoice {
  workFinancialEntryId: number;
  supplierId: number | null;
  supplierName: string;
  invoiceNumber: string | null;
  documentDate: string | null;
  totalAmount: number;
  workId: number | null;
  workName: string | null;
}

export interface ConfirmingPaymentInvoice {
  workFinancialEntryId: number;
  invoiceNumber: string | null;
  documentDate: string | null;
  amount: number;
}

// SupplierConfirmingHistoryDto [3.4.6] — agregado por fornecedor.
export interface SupplierConfirmingHistory {
  supplierId: number | null;
  supplierName: string;
  count: number;
  totalAmount: number;
  totalFinancialCost: number;
}

// CreateConfirmingPaymentCommand (amount/issueDate/dueDate opcionais → defaults no backend).
export interface CreateConfirmingPaymentPayload {
  workFinancialEntryId: number;
  amount?: number | null;
  issueDate?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  reference?: string | null;
  /** Outras faturas do mesmo fornecedor que o mesmo aviso paga. */
  additionalWorkFinancialEntryIds?: number[];
  confirmingBankId?: number | null;
}

// Quadro de avisos do banco lido e associado às faturas — POST /api/confirming-payments/read-notices.
export type ConfirmingNoticeStatus = "matched" | "ambiguous" | "unmatched" | "existing";

export interface ConfirmingNoticeInvoice {
  workFinancialEntryId: number;
  supplierName: string | null;
  invoiceNumber: string | null;
  documentDate: string | null;
  /** O que falta pagar. */
  amount: number;
  /** Pode juntar-se a outras num só aviso (sem pagamentos parciais). */
  canJoin: boolean;
}

export interface ConfirmingNoticeRow {
  reference: string | null;
  supplierName: string | null;
  amount: number | null;
  /** Dia em que o banco paga ao fornecedor. */
  paymentDate: string | null;
  /** Dia em que o banco debita a liquidação, quando o quadro o mostra. */
  settlementDate: string | null;
  status: ConfirmingNoticeStatus;
  reason: string;
  /** Conjuntos de faturas que dão o aviso; o primeiro é o proposto. */
  options: ConfirmingNoticeInvoice[][];
  /** As faturas por avisar do fornecedor, para escolher à mão. */
  supplierInvoices: ConfirmingNoticeInvoice[];
}

export interface ConfirmingNoticeProposal {
  rows: ConfirmingNoticeRow[];
  summary: string;
}

// Filtros da lista — GET /api/confirming-payments.
export interface ConfirmingPaymentFilters {
  workId?: number | null;
  supplierId?: number | null;
  statusCode?: string | null;
}

// Subconjunto de TreasurySettings necessário para pré-visualizar o custo do AP no cliente.
// Lido do endpoint de definições já existente (extra campos JSON são ignorados).
export interface ConfirmingSettings {
  confirmingPlafond: number;
  confirmingAnnualRatePercent: number;
  defaultSupplierPaymentDays: number;
  /** Dias entre o banco pagar ao fornecedor e debitar a liquidação do aviso. */
  confirmingTermDays?: number;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, readableError(text) || response.statusText);
  }

  return (await response.json()) as T;
}

/** O corpo de erro costuma ser um ProblemDetails JSON: mostra a primeira mensagem de validação, ou o detalhe. */
function readableError(text: string): string {
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object") {
      const first = Object.values(parsed.errors ?? {}).flat()[0];
      return (typeof first === "string" && first) || parsed.detail || parsed.title || text;
    }
  } catch {
    // Não é JSON — usa o texto tal como veio.
  }
  return text;
}

export function getConfirmingPayments(
  filters?: ConfirmingPaymentFilters,
): Promise<ConfirmingPayment[]> {
  const params = new URLSearchParams();
  if (filters?.workId != null) params.set("workId", String(filters.workId));
  if (filters?.supplierId != null) {
    params.set("supplierId", String(filters.supplierId));
  }
  if (filters?.statusCode && filters.statusCode.trim() !== "") {
    params.set("statusCode", filters.statusCode.trim());
  }
  const query = params.toString();
  return request<ConfirmingPayment[]>(
    `/api/confirming-payments${query ? `?${query}` : ""}`,
  );
}

export function getConfirmingPlafond(): Promise<ConfirmingPlafond> {
  return request<ConfirmingPlafond>("/api/confirming-payments/plafond");
}

export function getConfirmingHistoryBySupplier(): Promise<
  SupplierConfirmingHistory[]
> {
  return request<SupplierConfirmingHistory[]>(
    "/api/confirming-payments/history-by-supplier",
  );
}

export function getEligibleSupplierInvoices(): Promise<
  EligibleSupplierInvoice[]
> {
  return request<EligibleSupplierInvoice[]>(
    "/api/confirming-payments/eligible-invoices",
  );
}

export function createConfirmingPayment(
  payload: CreateConfirmingPaymentPayload,
): Promise<ConfirmingPayment> {
  return request<ConfirmingPayment>("/api/confirming-payments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateConfirmingPaymentStatus(
  id: number,
  statusCode: string,
): Promise<ConfirmingPayment> {
  return request<ConfirmingPayment>(`/api/confirming-payments/${id}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, statusCode }),
  });
}

/** Nº do aviso, datas (pagamento ao fornecedor / liquidação) e banco; o custo recalcula-se no backend. */
export function updateConfirmingPaymentDetails(
  id: number,
  details: { reference: string | null; issueDate: string; dueDate: string; confirmingBankId?: number | null },
): Promise<ConfirmingPayment> {
  return request<ConfirmingPayment>(`/api/confirming-payments/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, ...details }),
  });
}

export async function deleteConfirmingPayment(id: number): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/confirming-payments/${id}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}

/** Sobe o print ou o PDF do quadro de avisos do banco e recebe a proposta. Não cria nada. */
export function readConfirmingNotices(file: File): Promise<ConfirmingNoticeProposal> {
  const data = new FormData();
  data.append("file", file);
  return request<ConfirmingNoticeProposal>("/api/confirming-payments/read-notices", {
    method: "POST",
    body: data,
  });
}

// Rate/plafond de Confirming para o pré-cálculo do custo no modal "Novo AP".
export function getConfirmingBanks(): Promise<ConfirmingBank[]> {
  return request<ConfirmingBank[]>("/api/confirming-payments/banks");
}

export function saveConfirmingBanks(banks: ConfirmingBank[]): Promise<ConfirmingBank[]> {
  return request<ConfirmingBank[]>("/api/confirming-payments/banks", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ banks }),
  });
}

export function getConfirmingSettings(): Promise<ConfirmingSettings> {
  return request<ConfirmingSettings>("/api/settings/treasury");
}
